import { useEffect, useState } from "react";
import "./studentProgressJourney.css";
import { Link } from "react-router-dom";
import { formatDate, formatDuration } from "../format.js";
import { Pagination } from "../Pagination.js";
import {
  familyRepertoireHubMaterialPath,
  familyRepertoireMaterialOpenPath,
  getFamilyRepertoire,
  getFamilyRepertoireMaterials,
  REPERTOIRE_STATUS_LABELS,
  type RepertoireEntry,
  type RepertoireMaterial,
  type RepertoireStatus,
} from "../repertoire.js";
import { getFamilyStudentGoals } from "../studentGoals.js";
import { getFamilyPracticeTodos } from "../practiceTodos.js";
import { useAsync } from "../useAsync.js";
import { FamilyGoals } from "./FamilyGoals.js";
import { FamilyExamProjects } from "./FamilyExamProjects.js";

function pieceName(entry: RepertoireEntry): string {
  return entry.composer ? `${entry.composer} — ${entry.title}` : entry.title;
}

function RepertoireSection({
  heading,
  entries,
}: {
  heading: string;
  entries: RepertoireEntry[];
}) {
  if (entries.length === 0) return null;
  return (
    <section aria-labelledby={`family-repertoire-${heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <h2 id={`family-repertoire-${heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{heading}</h2>
      <div className="stack" style={{ gap: 10 }}>
        {entries.map((entry) => (
          <FamilyRepertoireCard key={entry.id} entry={entry} />
        ))}
      </div>
    </section>
  );
}

function materialLabel(material: RepertoireMaterial): string {
  if (material.presentationKind === "link") return "Web link";
  if (material.presentationKind === "hub_asset") return "Hub asset";
  return material.presentationKind.toUpperCase();
}

function FamilyRepertoireCard({ entry }: { entry: RepertoireEntry }) {
  const [showMaterials, setShowMaterials] = useState(false);

  return (
    <article className="panel">
      <div className="spread" style={{ gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div className="progress-title">
          <strong>{pieceName(entry)}</strong>
          {entry.workOrCollection && <div className="small muted">{entry.workOrCollection}</div>}
          <div className="small muted" style={{ marginTop: 4 }}>
            {REPERTOIRE_STATUS_LABELS[entry.status]}
            {entry.instrument && ` · ${entry.instrument}`}
            {entry.startedOn && ` · started ${formatDate(entry.startedOn)}`}
            {entry.completedOn && ` · completed ${formatDate(entry.completedOn)}`}
            {entry.archivedAt && ` · archived ${formatDate(entry.archivedAt)}`}
          </div>
          <div className="small muted progress-meta">
            {entry.createdByName ? `Added by ${entry.createdByName}` : "Tutor attribution unavailable"}
            {entry.createdAt && ` · recorded ${formatDate(entry.createdAt)}`}
          </div>
        </div>
        <button
          type="button"
          className="quiet small"
          aria-expanded={showMaterials}
          onClick={() => setShowMaterials((shown) => !shown)}
        >
          {showMaterials ? "Hide material" : "Teaching material"}
        </button>
      </div>
      {showMaterials && <FamilyRepertoireMaterials entry={entry} />}
    </article>
  );
}

function FamilyRepertoireMaterials({ entry }: { entry: RepertoireEntry }) {
  const studentId = entry.studentId;
  const materials = useAsync(
    () => getFamilyRepertoireMaterials(studentId, entry.id),
    [studentId, entry.id],
  );

  if (materials.loading && !materials.data) {
    return <p className="small muted" style={{ marginBottom: 0 }}>Loading teaching material…</p>;
  }
  if (materials.error) {
    return <div className="notice small" style={{ marginTop: 10 }} role="alert">
      Teaching material could not be loaded just now. <button type="button" className="quiet small" onClick={materials.reload}>Retry</button>
    </div>;
  }
  if (!materials.data || materials.data.length === 0) {
    return <p className="small muted" style={{ marginBottom: 0, marginTop: 10 }}>No teaching material has been shared for this piece yet.</p>;
  }

  return (
    <div className="stack" style={{ gap: 8, marginTop: 12 }}>
      {materials.data.map((material) => (
        <div className="row" key={material.id} style={{ justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <strong className="small">{material.title}</strong>
            <div className="small muted">
              {materialLabel(material)}
              {material.description ? ` · ${material.description}` : ""}
            </div>
          </div>
          {material.kind === "file" ? (
            <a
              className="button quiet small"
              href={familyRepertoireMaterialOpenPath(studentId, entry.id, material.id)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open
            </a>
          ) : material.kind === "hub_asset" && material.hubProvider === "groove" ? (
            <Link
              className="button quiet small"
              to={familyRepertoireHubMaterialPath(entry.id, material.id)}
            >
              Open
            </Link>
          ) : material.externalUrl ? (
            <a className="button quiet small" href={material.externalUrl} target="_blank" rel="noopener noreferrer">
              Open
            </a>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function FamilyPracticeReviewEvidence({ studentId }: { studentId: string }) {
  const practice = useAsync(() => getFamilyPracticeTodos(studentId), [studentId]);
  const evidence = (practice.data ?? [])
    .flatMap((todo) =>
      (todo.comments ?? []).map((comment, index) => ({
        key: `${todo.id}:${comment.publishedAt}:${index}`,
        todo,
        comment,
      })),
    )
    .sort(
      (a, b) =>
        Date.parse(b.comment.publishedAt) - Date.parse(a.comment.publishedAt) ||
        a.todo.title.localeCompare(b.todo.title),
    );

  return (
    <section aria-labelledby="family-practice-review-heading">
      <h2 id="family-practice-review-heading">Feedback on practice</h2>
      {practice.loading && !practice.data && (
        <p className="small muted">Loading practice feedback…</p>
      )}
      {practice.error && (
        <div className="notice" role="alert">
          <p className="small">Practice feedback could not be loaded just now.</p>
          <button type="button" className="quiet small" onClick={practice.reload}>
            Retry practice feedback
          </button>
        </div>
      )}
      {practice.data && evidence.length === 0 && !practice.error && (
        <div className="panel">
          <p className="small muted">No tutor feedback on practice has been shared yet.</p>
        </div>
      )}
      {evidence.length > 0 && (
        <div className="stack">
          {evidence.map(({ key, todo, comment }) => {
            const practiceSeconds = Number.isFinite(todo.practiceSeconds)
              ? todo.practiceSeconds
              : 0;
            return (
              <article className="panel" key={key}>
                <p className="small"><strong>{todo.title}</strong></p>
                <p className="small muted">
                  {comment.author} · {formatDate(comment.publishedAt)}
                </p>
                {todo.repertoireTitle && (
                  <p className="small muted">
                    Repertoire: {todo.repertoireComposer ? `${todo.repertoireComposer} — ` : ""}
                    {todo.repertoireTitle}
                  </p>
                )}
                <p className="small">{comment.comment}</p>
                {practiceSeconds > 0 && (
                  <p className="small muted">
                    Practice logged on this task: {formatDuration(practiceSeconds)} total
                  </p>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function FamilyProgress({ studentId }: { studentId: string }) {
  const [completedPage, setCompletedPage] = useState(1);
  const [archivedPage, setArchivedPage] = useState(1);
  const repertoire = useAsync(
    () => getFamilyRepertoire(studentId, completedPage, archivedPage),
    [studentId, completedPage, archivedPage],
  );
  const goals = useAsync(() => getFamilyStudentGoals(studentId), [studentId]);

  useEffect(() => {
    setCompletedPage(1);
    setArchivedPage(1);
  }, [studentId]);

  const loading = repertoire.loading && !repertoire.data && goals.loading && !goals.data;
  if (loading) return <p className="empty">Loading progress…</p>;

  const current = repertoire.data?.current ?? [];
  const completed = repertoire.data?.completed ?? null;
  const archived = repertoire.data?.archived ?? null;
  const byStatus = (status: RepertoireStatus) => current.filter((entry) => entry.status === status);
  const working = current.filter((entry) => entry.status === "learning" || entry.status === "polishing");
  const hasGoals = (goals.data?.active.length ?? 0) > 0 || (goals.data?.recentlyAchieved.length ?? 0) > 0;
  const hasRepertoire = current.length > 0 || (completed?.total ?? 0) > 0 || (archived?.total ?? 0) > 0;

  return (
    <div className="stack">
      <div>
        <h1>Progress</h1>
        <p className="lede">Your longer-term goals, the music you are working on, and milestones you have reached.</p>
      </div>

      {goals.error && (
        <div className="notice" role="alert">
          <p className="small">Goals could not be loaded just now. Repertoire is still available below.</p>
          <button type="button" className="quiet small" onClick={goals.reload}>Retry goals</button>
        </div>
      )}
      {goals.data && <FamilyGoals goals={goals.data} />}
      <FamilyExamProjects key={studentId} studentId={studentId} />

      <FamilyPracticeReviewEvidence key={studentId} studentId={studentId} />

      {repertoire.error && (
        <div className="notice" role="alert">
          <p className="small">Repertoire could not be loaded just now.</p>
          <button type="button" className="quiet small" onClick={repertoire.reload}>Retry repertoire</button>
        </div>
      )}

      {!goals.loading && !repertoire.loading && !hasGoals && !hasRepertoire && !goals.error && !repertoire.error && (
        <div className="panel">
          <p className="empty">No goals or repertoire have been shared here yet.</p>
        </div>
      )}

      {repertoire.data && (
        <>
          <RepertoireSection heading="Working on now" entries={working} />
          <RepertoireSection heading="Performance-ready" entries={byStatus("performance_ready")} />
          <RepertoireSection heading="Paused" entries={byStatus("paused")} />
          <RepertoireSection heading="Completed repertoire" entries={completed?.items ?? []} />
          <RepertoireSection heading="Archived repertoire" entries={archived?.items ?? []} />

          {completed && (
            <Pagination
              page={completed.page}
              pages={completed.pages}
              ariaLabel="Completed repertoire pagination"
              onPageChange={setCompletedPage}
            />
          )}
          {archived && (
            <Pagination
              page={archived.page}
              pages={archived.pages}
              ariaLabel="Archived repertoire pagination"
              onPageChange={setArchivedPage}
            />
          )}
        </>
      )}
    </div>
  );
}
